import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { type ReactNode, useEffect, useState } from "react";
import { OtsFormRow, OtsFormSection } from "../../components/OtsForm";
import { OtsFormDialog } from "../../components/OtsFormDialog";
import { OtsSegmentedControl } from "../../components/OtsSegmentedControl";
import type { OtsSelectOption } from "../../components/OtsSelect";
import {
  OtsFreightTypeSelect,
  OtsPersonSelect,
  OtsServiceTypeSelect,
  OtsTrailerSelect,
  OtsVehicleSelect,
} from "../../select/OtsEntitySelects";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { type BulkTransportChanges, type BulkTransportSide, hasBulkChanges } from "./bulk-transport-edit";

export type BulkTransportEditDialogProps = {
  open: boolean;
  /** How many tickets the edit covers, for the title. */
  count: number;
  /** Lists this region's drivers, equipment, and freight. */
  regionId?: string;
  onSubmit: (changes: BulkTransportChanges, side: BulkTransportSide) => void;
  onClose: () => void;
  submitting?: boolean;
  error?: ReactNode;
  /** Fixed choices instead of the region's records, for stories and tests. */
  options?: Partial<Record<"person" | "vehicle" | "trailer" | "freight" | "service", OtsSelectOption[]>>;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/**
 * Sets the same driver, equipment, or freight on many tickets at once. Only what is filled in changes; everything
 * else stays as each ticket has it.
 */
export const BulkTransportEditDialog = ({ open, count, regionId, onSubmit, onClose, submitting = false, error, options, brand, colorScheme }: BulkTransportEditDialogProps) => {
  const [changes, setChanges] = useState<BulkTransportChanges>({});
  const [side, setSide] = useState<BulkTransportSide>("both");
  useEffect(() => {
    if (!open) return;
    setChanges({});
    setSide("both");
  }, [open]);
  const set = (patch: BulkTransportChanges) => setChanges((current) => ({ ...current, ...patch }));
  const title = `Edit ${count === 1 ? "1 ticket" : `${count} tickets`}`;

  return (
    <OtsFormDialog
      open={open}
      onClose={onClose}
      onSubmit={() => hasBulkChanges(changes) && onSubmit(changes, side)}
      title={title}
      description="Only what you fill in changes; everything else stays as each ticket has it."
      icon={<PencilSquareIcon/>}
      formLabel={title}
      submitLabel="Save changes"
      submittingLabel="Saving…"
      submitDisabled={!hasBulkChanges(changes)}
      submitting={submitting}
      error={error}
      size="2xl"
      brand={brand}
      colorScheme={colorScheme}>
      <OtsSegmentedControl<BulkTransportSide>
        ariaLabel="Change"
        value={side}
        onChange={setSide}
        segments={[{ id: "both", label: "Pickups and deliveries" }, { id: "pickup", label: "Pickups" }, { id: "delivery", label: "Deliveries" }]}/>
      <OtsFormSection title="Driver and equipment">
        <OtsFormRow columns={3}>
          <OtsPersonSelect label="Driver" regionId={regionId} options={options?.person} value={changes.personId} onChange={(personId) => set({ personId })}/>
          <OtsVehicleSelect label="Truck" regionId={regionId} options={options?.vehicle} value={changes.vehicleId} onChange={(vehicleId) => set({ vehicleId })}/>
          <OtsTrailerSelect label="Trailer" regionId={regionId} options={options?.trailer} value={changes.trailerId} onChange={(trailerId) => set({ trailerId })}/>
        </OtsFormRow>
      </OtsFormSection>
      <OtsFormSection title="Freight">
        <OtsFormRow columns={2}>
          <OtsFreightTypeSelect label="Freight type" regionId={regionId} options={options?.freight} value={changes.waterTypeId} onChange={(waterTypeId) => set({ waterTypeId })}/>
          <OtsServiceTypeSelect label="Service type" regionId={regionId} options={options?.service} value={changes.serviceTypeId} onChange={(serviceTypeId) => set({ serviceTypeId })}/>
        </OtsFormRow>
      </OtsFormSection>
    </OtsFormDialog>
  );
};
